import { gql, useMutation } from '@apollo/client'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { makeViewModel } from '@/core/architecture'

import { Actions, InputParam, State } from './template'

const VERIFY_MUTATION = gql`
  mutation VerifyPartnerClientToken($input: VerifyPartnerClientTokenInput!) {
    verifyPartnerClientToken(input: $input) {
      valid
    }
  }
`

export const useViewModel = makeViewModel<State, Actions>(
  (): { state: State; actions: Actions } => {
    const location = useLocation()

    const searchParams = useMemo(
      () => new URLSearchParams(location.search),
      [location.search]
    )

    const action = searchParams.get('action') || ''
    const method = (searchParams.get('method') || 'post').toLowerCase()
    const inputsJson = searchParams.get('inputs') || '[]'
    const clientId = searchParams.get('clientId')
    const clientToken = searchParams.get('token')

    const inputs: InputParam[] = useMemo(() => {
      try {
        const parsed = JSON.parse(inputsJson)
        if (Array.isArray(parsed)) {
          return parsed
            .filter(v => v && typeof v.name === 'string')
            .map((v: any) => ({
              name: String(v.name),
              value: String(v.value ?? ''),
            }))
        }
        return []
      } catch (e) {
        return []
      }
    }, [inputsJson])

    const isValidAction = useMemo(() => {
      // actionのドメインが許可されているかをチェック
      // formのactionに指定するドメインを記載すること
      const allowedDomains = ['ticket.pia.jp']
      try {
        const u = new URL(action)
        if (u.search || u.hash) return false
        if (!allowedDomains.includes(u.hostname)) return false
        return true
      } catch {
        return false
      }
    }, [action])

    const [verified, setVerified] = useState<boolean | undefined>(undefined)

    const [verifyPartnerClientToken] = useMutation(VERIFY_MUTATION)

    return {
      state: {
        action,
        method,
        inputs,
        isValidAction,
        clientId,
        clientToken,
        verified,
      },
      actions: {
        verifyToken: async ({ clientId, token }) => {
          if (clientId.length === 0 || token.length === 0) {
            setVerified(false)
            return
          }
          try {
            // クエリで付与されたワンタイムトークンの検証
            // ワンタイムトークンは有効期限が5分、かつ1度使われたら再利用できない
            const { data } = await verifyPartnerClientToken({
              variables: {
                input: { clientId, token },
              },
            })
            setVerified(data.verifyPartnerClientToken.valid)
          } catch (e) {
            setVerified(false)
          }
        },
      },
    }
  }
)
